import {
  TrendingUp, Users, Repeat, BarChart3, MessageSquare, Wallet, Server,
  Sparkles, LayoutDashboard, Shield, AlertTriangle, Activity, UserCog, Feather,
} from "lucide-react";

import TrendSection from "@/components/sections/TrendSection";
import CreditsSection from "@/components/sections/CreditsSection";
import AccountRoleSection from "@/components/sections/AccountRoleSection";
import RetentionSection from "@/components/sections/RetentionSection";
import ToolUsageSection from "@/components/sections/ToolUsageSection";
import CxSection from "@/components/sections/CxSection";
import FinopsSection from "@/components/sections/FinopsSection";
import DevopsSection from "@/components/sections/DevopsSection";
import MyStyleSection from "@/components/sections/MyStyleSection";

// 백오피스 정보구조(IA) 단일 소스 (2026-08-21 IA 정리)
//
// 이번 재편(2026-08-21):
//   - Admin 을 운영 계정 관리 중심으로 축소: 사용자 관리 / 계정 Role / 크레딧
//   - 고객 문의(CxSection) → /tickets 최상위 영역으로 이동 (기존 컴포넌트 재사용)
//   - DevOps(DevopsSection) → /system 최상위 영역으로 이동 (기존 컴포넌트 재사용)
//
// 탭 추가/이름변경/그룹이동은 아래 SECTIONS 한 곳만 고치면 세 곳(대시보드·관리자·소개)이
// 함께 따라온다.

export const GROUPS = {
  briefing:  { path: "/briefing",  label: "오늘의 브리핑", icon: Sparkles },
  dashboard: { path: "/dashboard", label: "Dashboard",    icon: LayoutDashboard },
  admin:     { path: "/admin",     label: "Admin",        icon: Shield },
  tickets:   { path: "/tickets",   label: "오류 제보",    icon: AlertTriangle },
  system:    { path: "/system",    label: "시스템 오류",   icon: Activity },
};

// id       : ?tab= 파라미터 값이자 안내 카드 링크의 앵커. 그룹이 바뀌어도 그대로 유지된다.
// group    : 소속 URL(GROUPS 의 키). 대시보드/관리자 페이지가 이 값으로 자기 탭을 필터한다.
// label    : 탭 버튼 + 소개 카드 제목 공용 표기.
// Component : 탭 본문.
// icon/tone/badge/what/examples : 소개(/about) 안내 카드 전용 메타.
export const SECTIONS = [
  // ── Dashboard (4개 분석 영역) ──
  {
    id: "trend", group: "dashboard", label: "매출·마진 추이", Component: TrendSection,
    icon: TrendingUp, tone: "#2563eb", badge: "여기부터",
    what: "돈의 흐름. 직접비 기여이익 = 매출 − 추적된 직접비와 그 구성을 본다",
    examples: ["매출·지출·기여이익·활성 사용자 KPI", "손익 추이", "충전 내역", "지출 세부내역"],
  },
  {
    id: "toolUsage", group: "dashboard", label: "사용량", Component: ToolUsageSection,
    icon: BarChart3, tone: "#f97316",
    what: "어떤 기능·모델을 얼마나 쓰는지. 같은 사용량을 두 각도로 본다",
    examples: ["도구별 — 무엇을 했나", "모델별 — 어떤 AI가 일했나", "호출 수 · 토큰 · 캐시히트%"],
  },
  {
    id: "mystyle", group: "dashboard", label: "MyStyle", Component: MyStyleSection,
    icon: Feather, tone: "#0ea5e9",
    what: "MyStyle 운영 현황 — 채택률·성능·정합성(정본: mystyle_perf 로그)",
    examples: ["요청 수 · 실제 적용률", "Intent Mismatch(UI↔backend)", "total latency p50/p95 · 캐시 히트율"],
  },
  {
    id: "retention", group: "dashboard", label: "리텐션", Component: RetentionSection,
    icon: Repeat, tone: "#8b5cf6",
    what: "가입자가 남는지 떠나는지, 재사용과 결제 전환",
    examples: ["가입 주차별 잔존율(코호트)", "잔액 소진 후 행동 4갈래", "이탈 위험 사용자"],
  },
  {
    id: "finops", group: "dashboard", label: "FinOps", Component: FinopsSection,
    icon: Wallet, tone: "#0891b2",
    what: "AI 원가 대비 마진 건전성 감시와 기여이익 시뮬레이션",
    examples: ["경영 시나리오(기여이익 시뮬레이션)", "계기판 — 마진·지출 경고등", "캐시 효율", "정책·규칙"],
  },

  // ── Admin (3개 운영 영역 — 계정 중심) ──
  {
    id: "users", group: "admin", label: "사용자 관리", Component: CreditsSection,
    icon: Users, tone: "#10b981",
    what: "사용자 명단 확인과 크레딧 지급·회수 등 운영 액션",
    examples: ["이메일·User ID 검색", "+/− 크레딧 조정", "일괄 지급", "사용자별 상세 내역"],
  },
  {
    id: "accountRole", group: "admin", label: "계정 Role", Component: AccountRoleSection,
    icon: UserCog, tone: "#7c3aed",
    what: "계정에 역할을 부여·해제해 집계 대상을 관리한다",
    examples: ["admin — 관리자 권한·지표 제외", "test — 테스트 계정·지표 제외", "none — 일반 고객(기본)"],
  },
  {
    id: "credits", group: "admin", label: "크레딧", Component: CreditsSection,
    icon: Wallet, tone: "#059669",
    what: "크레딧 잔액·충전·사용 현황 조회 및 일괄 지급",
    examples: ["잔액 현황", "충전 내역", "일괄 NC 지급/회수", "소진 임박 사용자"],
  },

  // ── 오류 제보 (기존 CxSection 재사용) ──
  {
    id: "cx", group: "tickets", label: "고객 문의", Component: CxSection,
    icon: MessageSquare, tone: "#ec4899",
    what: "고객 문의 응대와 의견 확인 창구",
    examples: ["문의(티켓) — 답변·상태 관리", "의견(VOC) — 피드백 모음", "새 문의 실시간 배지"],
  },

  // ── 시스템 오류 (기존 DevopsSection 재사용) ──
  {
    id: "devops", group: "system", label: "서비스 상태", Component: DevopsSection,
    icon: Server, tone: "#475569",
    what: "서비스 안정성과 배포·개발 상태 점검",
    examples: ["배포 빈도 · CI 성공률", "안정성 — 함수별 에러율", "속도·규율 — 커밋/배포 리듬"],
  },
];

// 한 그룹(대시보드/관리자)에 속한 탭만 순서대로 뽑는다.
export const sectionsByGroup = (group) => SECTIONS.filter((s) => s.group === group);

// 소개 카드가 해당 탭으로 바로 넘어갈 때 쓰는 링크(예: /dashboard?tab=trend).
export const sectionPath = (section) => `${GROUPS[section.group].path}?tab=${section.id}`;
